<template>
  <div ref="blockRef" class="mx-auto px-4 max-w-6xl">
    <h2
      v-if="block.title"
      v-html="formatTitle(block.title)"
      :class="`
        text-[clamp(1.5rem,-2.25rem+18.75vw,3.75rem)] leading-none
        ${block.title_color?.value}
        ${block.title_centered ? 'text-center' : 'max-w-md'}
      `"
      data-gsap-target="title"
    />

    <div
      v-if="block.four_usps_columns?.length"
      class="flex gap-4 mt-8 -mr-4 pr-4 overflow-x-scroll sm:mt-12 lg:grid lg:grid-cols-4 lg:mr-0 lg:pr-0 lg:overflow-x-auto"
    >
      <div
        v-for="(column, index) in block.four_usps_columns"
        :key="column.id"
        :class="`
          group relative flex flex-col shrink-0 p-4 min-w-56 rounded-4xl text-purple-dark
          ${column.column_text_on_hover ? 'bg-white' : ''}
          ${!column.column_image ? `justify-center aspect-[4/3] ${getColumnClass(index)}` : ''}
        `"
        data-gsap-target="column"
      >
        <div
          v-if="column.column_text_on_hover"
          class="absolute top-0 left-0 flex flex-col items-center justify-center h-full w-full p-4 text-center opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        >
          <div
            v-if="column.column_text"
            class="text-lg font-serif text-center leading-tight md:text-xl lg:text-2xl"
          >
            {{ column.column_text }}
          </div>

          <div class="mt-2 text-xs lg:mt-4 lg:text-lg xl:leading-tight">
            {{ column.column_text_on_hover }}
          </div>
        </div>

        <img
          v-if="column.column_image"
          :src="column.column_image.sm.url"
          :srcSet="`
            ${column.column_image.sm.url} ${column.column_image.sm.width}w
          `"
          :alt="column.column_image.sm.alt"
          class="h-full w-full object-cover aspect-[10/11] rounded-3xl transition-opacity duration-300 group-hover:opacity-0"
          sizes="
            (min-width: 1152px) 236px,
            (min-width: 1024px) 20vw,
            (min-width: 768px) 25vw,
            45vw
          "
          :width="column.column_image.sm.width"
          :height="column.column_image.sm.height"
          loading="lazy"
          draggable="false"
        >

        <div
          v-if="column.column_text"
          :class="`
            font-serif text-xl text-center transition-opacity duration-300 group-hover:opacity-0 lg:text-2xl lg:leading-tight
            ${column.column_image ? 'mt-2 lg:mt-6' : ''}
          `"
        >
          {{ column.column_text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TitleFourUspsColumns } from '~/statamic/types/block';

const props = defineProps<{
  block: TitleFourUspsColumns;
}>();

let ctx: gsap.Context | null;
const blockRef = ref();

const loopedColumnClasses = computed(() => {
  const columnClasses = [
    '!bg-purple-light',
    '!bg-blue-light',
    '!text-white !bg-purple-dark',
    '!bg-white',
  ];
  const loops = Math.ceil(props.block.four_usps_columns.length / columnClasses.length);
  let classes = [...columnClasses];

  for (let i = 0; i < loops; i += 1) {
    const isEven = i % 2 === 0;
    const columnClassesForLoop = isEven ? [...columnClasses].reverse() : [...columnClasses];

    classes = classes.concat(columnClassesForLoop);
  }

  return classes;
});

const getColumnClass = (index) => {
  return loopedColumnClasses.value[index];
};

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const titleLeft = self.selector('[data-gsap-target="title"]')[0];
    const titleRight = self.selector('[data-gsap-target="title"] > .text-right')[0];
    const columns = self.selector('[data-gsap-target="column"]');

    const timeline = useGsap.timeline({
      scrollTrigger: {
        start: 'top bottom',
        trigger: blockRef.value,
      },
    });

    if (titleLeft) {
      timeline.from(titleLeft, {
        x: -100,
        autoAlpha: 0,
      });
    }

    if (titleRight) {
      timeline.from(titleRight, {
        x: 100,
        autoAlpha: 0,
      }, '-=0.25');
    }

    if (columns?.length) {
      timeline.from(columns, {
        x: 100,
        y: 100,
        stagger: 0.25,
        autoAlpha: 0,
      });
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
